import React, { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import Image from "next/image"
import SettingSection from "../../shared/SettingSection"
import EmptySettingBox from "../../shared/EmptySettingBox.container"
import KosmoButton from "@/UI/Buttons/KosmoButton"
import SettingContentWrapper from "../../shared/SettingContentWrapper"
import SandboxDisclaimer from "../components/SandboxDisclaimer"
import APIKeysAddEditModal from "./components/APIKeysAddModal"
import { deleteCredentials, getCredentials, postCredentials } from "@/api/credentials"
import { IApiKeys } from "@/types/credentials.types"
import { isEmpty } from "lodash"
import ApiKeysTable from "./components/ApiKeysTable"
import { SandboxContext } from "@/layout/Nav/SandboxSwitcher"
import { Loader } from "@/UI"
import { useRecoilValue } from "recoil"
import { userAtom } from "@/atoms/userAtom"
import { faPlus } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

interface APIKeysProps {}

const APIKeys: React.FC<APIKeysProps> = () => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [apiKeys, setApiKeys] = useState<IApiKeys[]>([])

  const sandbox = useContext(SandboxContext)
  const user = useRecoilValue(userAtom)

  const handleCreateKeys: (keyName: string) => Promise<IApiKeys> = async (keyName) => {
    try {
      const { data } = await postCredentials(keyName)
      return data
    } catch (error) {
      console.log("error creating keys", error)
    }
  }

  const handleDeleteApiKeys: (id: string) => Promise<void> = async (id) => {
    setIsLoading(true)
    try {
      const { data } = await deleteCredentials(id)
      const newApiKeys = apiKeys?.filter((key) => key.id !== data?.id)
      setApiKeys(newApiKeys)
    } catch (error) {
      console.log("error deleting keys", error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const fetchCredentials = async () => {
    setIsLoading(true)
    try {
      const { data } = await getCredentials()
      setApiKeys(data)
    } catch (error) {
      console.log("error creating keys", error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchCredentials()
  }, [sandbox.enabled, user.org.id])

  return (
    <SettingSection
      title={t("developers.apiKeys")}
      actionButton={
        isEmpty(apiKeys) ? undefined : (
          <APIKeysAddEditModal
            title={t("developers.APIKeys.createKeys")}
            button={
              <KosmoButton id="create-new-api-keys-btn" type="primary">
                <div className="flex items-center gap-x-2">
                  <FontAwesomeIcon icon={faPlus} color="#FFF" />
                  <p>{t("developers.APIKeys.createSecretKey")}</p>
                </div>
              </KosmoButton>
            }
            handleCreateKeys={handleCreateKeys}
            fetchCredentials={fetchCredentials}
          />
        )
      }
    >
      <SandboxDisclaimer withToggle />
      <SettingContentWrapper>
        {isLoading ? (
          <div className="flex justify-center items-center h-full">
            <Loader fontSize={46} />
          </div>
        ) : isEmpty(apiKeys) ? (
          <EmptySettingBox
            id="empty-api-keys"
            icon={
              <Image
                src={"/assets/images/developers/emptyKeyIllu.svg"}
                alt="logo"
                width={80}
                height={80}
              />
            }
            description={t("developers.APIKeys.startCreatingSecretKey")}
            button={
              <APIKeysAddEditModal
                title={t("developers.APIKeys.createKeys")}
                button={
                  <KosmoButton id="create-first-api-keys-btn" type="primary">
                    <div className="flex items-center gap-x-2">
                      <FontAwesomeIcon icon={faPlus} color="#FFF" />
                      <p>{t("developers.APIKeys.createSecretKey")}</p>
                    </div>
                  </KosmoButton>
                }
                handleCreateKeys={handleCreateKeys}
                fetchCredentials={fetchCredentials}
              />
            }
          />
        ) : (
          <ApiKeysTable apiKeys={apiKeys} deleteApiKeys={handleDeleteApiKeys} />
        )}
      </SettingContentWrapper>
    </SettingSection>
  )
}

export default APIKeys
